import { Injectable } from '@angular/core';
import { QueryEntity } from '@datorama/akita';
import { GameStore, GameState } from './game.store';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { Game } from './models/game.model';
import { GameDataSource } from './models/game-data-source.model';
import { TicketQuery } from '../tickets/state/ticket.query';
import moment from 'moment/moment';

@Injectable({ providedIn: 'root' })
export class GameQuery extends QueryEntity<GameState> {
  constructor(protected store: GameStore, private ticketQuery: TicketQuery) {
    super(store);
  }

  /**
   * Select all past and present games for the season.
   */
  selectAllGames(): Observable<Game[]> {
    return this.selectAll();
  }

  /**
   * Select all games filtered
   * by the given data source type.
   * @param dataSourceType type of game data
   * @returns the type of game data depending
   * on data source type passed.
   */
  selectAllGamesByDataSource(dataSourceType: GameDataSource): Observable<Game[]> {
    return this.selectAll({ filterBy: ({ dataSource }) => dataSource === dataSourceType });
  }

  /**
   * Select game by data source and game number.
   * @param dataSourceType data type of game
   * @param gameNumber game number of game
   * @returns returns the game by game number and data type.
   */
  selectGameByDataSourceAndGameNumber(dataSourceType: GameDataSource, gameNumber: number) {
    return this.selectAll().pipe(
      map((games) => {
        if (!!games) {
          return games.filter((game) => game.dataSource === dataSourceType && game.GameNumber === gameNumber)[0];
        }

        return undefined;
      })
    );
  }

  /**
   * Will return the next game from the requested game date
   * type passed, e.g. if passed club type then this will
   * return the next game from the game data from the club.
   * @param dataSourceType required data source type for next game
   * @returns the next upcoming game for the season.
   */
  selectNextGame(dataSourceType: GameDataSource) {
    return this.selectAllGamesByDataSource(dataSourceType).pipe(
      map((games) => {
        if (games.length === 0) {
          return null;
        }
        let gamesUpcoming = games.filter((game) => moment.utc(game.Date).isAfter(moment.utc().startOf('day')));
        gamesUpcoming = gamesUpcoming.sort((first, second) => moment(first.Date).diff(moment.utc(second.Date)));

        return gamesUpcoming[0];
      })
    );
  }

  /**
   * Select all past and present games for the season along with their ticket information.
   */
  selectGamesWithTickets(dataSourceType: GameDataSource): Observable<Game[]> {
    return this.selectAll({ filterBy: ({ dataSource }) => dataSource === dataSourceType }).pipe(
      map((games) =>
        games.map((game) => {
          let ticketInfo = this.ticketQuery.getTicketInformation(game.GameNumber);
          return { ...game, ...ticketInfo };
        })
      ),
      map((games) => {
        return this._getDistinctGames(games, 'GameNumber');
      })
    );
  }

  /**
   * Select all upcoming games with ticket data
   * @param dataSourceType required data source type for upcoming games
   * with ticket data defaulted to external ref dat
   * @returns list of upcoming games with ticket data
   */
  selectUpcomingGamesWithTicketData(dataSourceType: GameDataSource = GameDataSource.ExternalRef) {
    return this.selectGamesWithTickets(dataSourceType).pipe(
      map((games) => games.filter((game) => moment.utc(game.Date).endOf('day').isAfter(moment.utc())))
    );
  }

  /**
   * Select all past games for the
   * season with ticket data.
   * @param dataSourceType required data source type for past games
   * with ticket data defaulted to external ref data
   * @returns list of past games with ticket data
   */
  selectPastGamesOnlyWithTicketData(dataSourceType: GameDataSource = GameDataSource.ExternalRef): Observable<Game[]> {
    return this.selectGamesWithTickets(dataSourceType).pipe(
      map((games) => games.filter((game) => moment.utc(game.Date).endOf('day').isBefore(moment.utc())))
    );
  }

  /**
   * Select upcoming games
   * with no ticket info
   * @param dataSourceType required data source type for upcoming games
   * without ticket data
   * @returns list of upcoming games without ticket data
   */
  selectUpcomingGames(dataSourceType: GameDataSource) {
    return this.selectAllGamesByDataSource(dataSourceType).pipe(
      map((games) => games.filter((game) => moment.utc(game.Date).endOf('day').isAfter(moment.utc())))
    );
  }

  /**
   * Select past games
   * with no ticket info
   * @param dataSourceType required data source type for past games
   * without ticket data
   * @returns list of past games without ticket data
   */
  selectPastGames(dataSourceType: GameDataSource) {
    return this.selectAllGamesByDataSource(dataSourceType).pipe(
      map((games) => games.filter((game) => moment.utc(game.Date).endOf('day').isBefore(moment.utc())))
    );
  }

  /**
   * Takes an array of Games and groups by Game Number, then selects first item
   */
  private _getDistinctGames(data: Game[], key: string): Game[] {
    let grouping = data.reduce((object: any, value: any) => {
      object[value[key]] = object[value[key]] || value;
      return object;
    }, {});

    return Object.values(grouping);
  }
}
